import dayjs from 'dayjs'
import {Diagnosis, DiagnosisVersion} from '@/features/projects/types'

/**
 * Date ICD-10-IM becomes the mandatory classification. Until then ICD-9-CM and ICD-10-IM coexist
 * and codes are picked in ICD-9; from this date on they are picked in ICD-10.
 */
export const ICD10_MANDATORY_FROM = '2027-01-01'

/**
 * Classification in force. Both selects stay searchable during the coexistence, so this only decides
 * whether ICD-9 may still be picked: from ICD10_MANDATORY_FROM the ICD-9 select goes read-only and is
 * filled in from the ICD-10 instead.
 */
export const getDiagnosisSelectionVersion = (): DiagnosisVersion =>
    dayjs().isBefore(ICD10_MANDATORY_FROM) ? 'ICD9' : 'ICD10'

/**
 * Counterpart auto-filled in the other column when a code is picked. ICD-9 maps to up to 47 ICD-10
 * codes (23% of the mapped ICD-9s are 1:N), so one has to be chosen: this mirrors the backend
 * DiagnosisService.resolveToVersion — sort by code, take the first — so the code shown here is the
 * same one the SIAR export would derive on its own. The user can still refine it in its own select.
 */
export const getFirstMappedCode = (diagnosis?: Diagnosis | null): Diagnosis | null =>
    [...(diagnosis?.mappedCodes ?? [])].sort((a, b) => a.code.localeCompare(b.code)).at(0) ?? null

/**
 * Shows a stored diagnosis as the ICD-9 / ICD-10 pair both selects display. Only one code is ever
 * persisted — the one the user picked, whichever classification they picked it in — so the other
 * side is rebuilt from its mappedCodes.
 *
 * This is what keeps the saved code honest in both directions. Always persisting the ICD-9 would
 * drop a hand-picked ICD-10 (23% of the mapped ICD-9s are 1:N, up to 47 candidates) and let the
 * backend re-derive it by code order — a clinical error. Always persisting the ICD-10 would instead
 * migrate a legacy ICD-9 record on any unrelated edit, like fixing a start date. Keeping the picked
 * code as-is does neither, and needs no extra state: the code carries its own version.
 */
export const splitDiagnosisPair = (diagnosis?: Diagnosis | null) => {
    if (!diagnosis) {
        return {icd9: null, icd10: null}
    }

    const counterpart = getFirstMappedCode(diagnosis)
    return diagnosis.version == 'ICD10' ? {icd9: counterpart, icd10: diagnosis} : {icd9: diagnosis, icd10: counterpart}
}

/** Display names of the classifications. Proper nouns, identical in every locale. */
export const DIAGNOSIS_VERSION_LABELS: Record<DiagnosisVersion, string> = {
    ICD9: 'ICD-9',
    ICD10: 'ICD-10'
}
